// File: UserSessionsTab.tsx
import React, { useState } from 'react';
import {
  Users,
  Clock,
  Activity,
  Calendar,
  User,
  X
} from 'lucide-react';
import SessionDetailModal from '../../components/AdminPanel/SessionDetailModal';

// Interfaces
interface UserSession {
  id: string;
  userId: string;
  userEmail: string;
  userType: "jobseeker" | "hr" | "admin";
  loginTime: Date| number| any;
  logoutTime: any | number|null;
  sessionDuration: number | null;
  createdAt: any;
}

interface SessionStats {
  totalSessions: number;
  activeSessions: number;
  avgSessionDuration: number;
  totalUsersToday: number;
}

// Grouped session interface
interface GroupedSession {
  userId: string;
  userEmail: string;
  userType: "jobseeker" | "hr" | "admin";
  totalSessions: number;
  activeSessions: number;
  lastLoginTime: Date | null;
  lastLogoutTime: Date | null;
  lastSessionDuration: number | null;
  isActive: boolean;
}

// Props interface
interface UserSessionsTabProps {
  sessions: GroupedSession[];
  allRawSessions: UserSession[]; // All raw sessions for detailed view
  sessionStats: SessionStats;
  sessionFilter: string;
  setSessionFilter: (filter: string) => void;
  userTypeFilter: string;
  setUserTypeFilter: (filter: string) => void;
  fetchUserSessions: () => void;
}

const UserSessionsTab: React.FC<UserSessionsTabProps> = ({
  sessions,
  allRawSessions,
  sessionStats,
  sessionFilter,
  setSessionFilter,
  userTypeFilter,
  setUserTypeFilter,
  fetchUserSessions
}) => {
  const [selectedUser, setSelectedUser] = useState<GroupedSession | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Helper function to format duration
  const formatDuration = (milliseconds: number | null) => {
    if (!milliseconds) return 'Still Active';
    const minutes = Math.floor(milliseconds / 60000);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  // Helper function to format time
  const formatTime = (date: Date | null) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Helper function to format date only
  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  // Handle click on total sessions
  const handleSessionClick = (userSession: GroupedSession) => {
    setSelectedUser(userSession);
    setShowModal(true);
  };

  // Get last 10 sessions for a specific user
  const getUserSessions = (userId: string) => {
    if (!allRawSessions || allRawSessions.length === 0) {
      return [];
    }
    return allRawSessions
      .filter(session => session.userId === userId)
      .sort((a, b) => {
        const dateA = a.loginTime instanceof Date ? a.loginTime.getTime() : 0;
        const dateB = b.loginTime instanceof Date ? b.loginTime.getTime() : 0;
        return dateB - dateA; // Sort descending (latest first)
      })
      .slice(0, 10); // Take only last 10
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{sessionStats.totalSessions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Activity className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{sessionStats.activeSessions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Avg Duration</p>
              <p className="text-2xl font-bold text-gray-900">{sessionStats.avgSessionDuration}m</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Calendar className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Users Today</p>
              <p className="text-2xl font-bold text-gray-900">{sessionStats.totalUsersToday}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-medium text-gray-900">Session Activity</h3>
          <div className="flex flex-wrap gap-2">
            <select
              value={sessionFilter}
              onChange={(e) => setSessionFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="active">Active Only</option>
            </select>
            <select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="all">All Users</option>
              <option value="hr">HR Only</option>
              <option value="jobseeker">Candidates Only</option>
            </select>
            <button
              onClick={fetchUserSessions}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="bg-white shadow-sm border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Sessions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active Sessions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Logout
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Session Duration
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <Clock className="w-12 h-12 text-gray-300 mb-2" />
                      <p className="text-lg font-medium">No sessions found</p>
                      <p className="text-sm">Try adjusting your filter or check back later</p>
                    </div>
                  </td>
                </tr>
              ) : (
                sessions.map((userSession, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-red-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {userSession.userEmail}
                          </div>
                          <div className="text-sm text-gray-500">
                            {userSession.userType === "hr" ? "HR" : "Candidate"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <button
                        onClick={() => handleSessionClick(userSession)}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {userSession.totalSessions}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {userSession.activeSessions}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTime(userSession.lastLoginTime)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTime(userSession.lastLogoutTime)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {userSession.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Still Active
                        </span>
                      ) : (
                        formatDuration(userSession.lastSessionDuration)
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Session Detail Modal */}
      {selectedUser && (
        <SessionDetailModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          userEmail={selectedUser.userEmail}
          userType={selectedUser.userType}
          sessions={getUserSessions(selectedUser.userId)}
        />
      )}
    </div>
  );
};

export default UserSessionsTab;
